import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import {
  IntegrityMonitorDetailedAssignationData,
  IntegrityMonitoringRuleSetsAndPolicies,
  IntegrityMonitorRulesAppWebComponentSchema,
} from '../types';
import {
  WEB_COMPONENT_COMPANY_SELECTOR,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
  WEB_COMPONENT_GZ_TITLE,
  WEB_COMPONENT_INTEGRITY_MONITOR_RULE_SET_FORM,
  WEB_COMPONENT_INTEGRITY_MONITOR_DEFAULT_RULE_CONFIGURATION,
  WEB_COMPONENT_INTEGRITY_MONITOR_RULES_GRID,
  WEB_COMPONENT_INTEGRITY_MONITOR_RULES_SMART_VIEWS,
  WEB_COMPONENT_INTEGRITY_MONITOR_RULES_ADD_CUSTOM_RULE,
  WEB_COMPONENT_INTEGRITY_MONITOR_ASSIGN_RULES_FORM,
} from '../../../../webcomponents.constants';
import { SmartViewItemConfiguration } from '../../../../grid/gridSmartViews/types';
import { Column, ColumnKeys, Filter } from '../../integrityMonitorRulesGrid/types';
import { ViewType } from '../../integrityMonitorRulesSmartViews/types';
import { ViewConfiguration } from '../../../../grid/gridView/types';
import { ResizeDirection, TemplateState } from '../../../../types/common/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzPushBodyLayoutWebComponent } from '../../../../ui/gzPushBodyLayout';
import { CompanySelectorWebComponent } from '../../../../companySelector';
import { IntegrityMonitorRulesSmartViewsWebComponent } from '../../integrityMonitorRulesSmartViews';
import { IntegrityMonitorRulesGridWebComponent } from '../../integrityMonitorRulesGrid';
import { IntegrityMonitorRuleSetFormWebComponent } from '../../integrityMonitorRuleSetForm';
import { IntegrityMonitorAddCustomRulePanelWebComponent } from '../../integrityMonitorCustomRules/imAddCustomRulePanel';
import { IntegrityMonitorDefaultRuleConfigurationWebComponent } from '../../integrityMonitorDefaultRuleConfiguration';
import { IntegrityMonitorAssignRulesFormWebComponent } from '../../integrityMonitorAssignRulesForm';
import { IMConstants } from '../../../common/utils/IMConstants';
import { Target } from '@nirvana/shared/company-helper/nestjs';

export class IntegrityMonitorRulesAppView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $pushBodyLayout: GzPushBodyLayoutWebComponent;
    $title: GzTitleWebComponent;
    $companySelector: CompanySelectorWebComponent;
    $smartViewsTitle: GzTitleWebComponent;
    $smartViews: IntegrityMonitorRulesSmartViewsWebComponent;
    $integrityMonitorGrid: IntegrityMonitorRulesGridWebComponent;
    $integrityMonitorRuleSetForm: IntegrityMonitorRuleSetFormWebComponent;
    $integrityMonitorAssignRulesForm: IntegrityMonitorAssignRulesFormWebComponent;
    $integrityMonitorCustomRuleForm: IntegrityMonitorAddCustomRulePanelWebComponent;
    $integrityMonitorDefaultRuleCustomizationForm: IntegrityMonitorDefaultRuleConfigurationWebComponent;
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$pushBodyLayout = this.shadow.querySelector(WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT);
    this.dom.$title = this.shadow.querySelector('#title');
    this.dom.$companySelector = this.shadow.querySelector(
      WEB_COMPONENT_COMPANY_SELECTOR
    ) as CompanySelectorWebComponent;
    this.dom.$smartViewsTitle = this.dom.$pushBodyLayout.querySelector(
      `[slot=push-body-sidenav-title] ${WEB_COMPONENT_GZ_TITLE}`
    ) as GzTitleWebComponent;
    this.dom.$smartViews = this.dom.$pushBodyLayout.querySelector(
      WEB_COMPONENT_INTEGRITY_MONITOR_RULES_SMART_VIEWS
    ) as IntegrityMonitorRulesSmartViewsWebComponent;
    this.dom.$integrityMonitorGrid = this.dom.$pushBodyLayout.querySelector(
      WEB_COMPONENT_INTEGRITY_MONITOR_RULES_GRID
    ) as IntegrityMonitorRulesGridWebComponent;
    this.dom.$integrityMonitorRuleSetForm = this.shadow.querySelector(
      WEB_COMPONENT_INTEGRITY_MONITOR_RULE_SET_FORM
    ) as IntegrityMonitorRuleSetFormWebComponent;
    this.dom.$integrityMonitorAssignRulesForm = this.shadow.querySelector(
      WEB_COMPONENT_INTEGRITY_MONITOR_ASSIGN_RULES_FORM
    ) as IntegrityMonitorAssignRulesFormWebComponent;
    this.dom.$integrityMonitorCustomRuleForm = this.shadow.querySelector(
      WEB_COMPONENT_INTEGRITY_MONITOR_RULES_ADD_CUSTOM_RULE
    ) as IntegrityMonitorAddCustomRulePanelWebComponent;
    this.dom.$integrityMonitorDefaultRuleCustomizationForm = this.shadow.querySelector(
      WEB_COMPONENT_INTEGRITY_MONITOR_DEFAULT_RULE_CONFIGURATION
    );

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
    this.injectDependenciesInForms();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getIntegrityMonitorStrings().integrityMonitorRules;
  }

  setTranslatedStrings() {
    this.dom.$smartViewsTitle.textContent = this.translations.smartViews.panelTitle.toUpperCase();
    this.dom.$title.innerText = this.translations.gridTitle;
  }

  private injectDependenciesInForms() {
    if (this.dom.$integrityMonitorRuleSetForm.injectDependencies) {
      this.dom.$integrityMonitorRuleSetForm.injectDependencies(this.dependencies);
      this.dom.$integrityMonitorRuleSetForm.start().catch(console.error);
    }

    if (this.dom.$integrityMonitorAssignRulesForm.injectDependencies) {
      this.dom.$integrityMonitorAssignRulesForm.injectDependencies(this.dependencies);
      this.dom.$integrityMonitorAssignRulesForm.start().catch(console.error);
    }

    if (this.dom.$integrityMonitorCustomRuleForm.injectDependencies) {
      this.dom.$integrityMonitorCustomRuleForm.injectDependencies(this.dependencies);
      this.dom.$integrityMonitorCustomRuleForm.start().catch(console.error);
    }

    if (this.dom.$integrityMonitorDefaultRuleCustomizationForm.injectDependencies) {
      this.dom.$integrityMonitorDefaultRuleCustomizationForm.injectDependencies(this.dependencies);
      this.dom.$integrityMonitorDefaultRuleCustomizationForm.start().catch(console.error);
    }
  }

  setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  async buildSuccessView(data: IntegrityMonitorRulesAppWebComponentSchema) {
    this.resetTemplateState(this.dom.$wrapper);

    this.dom.$pushBodyLayout.resizable = ResizeDirection.RIGHT;
    this.dom.$integrityMonitorRuleSetForm.isVisible = false;
    this.dom.$integrityMonitorAssignRulesForm.isVisible = false;
    this.dom.$integrityMonitorCustomRuleForm.isVisible = false;
    this.dom.$integrityMonitorDefaultRuleCustomizationForm.isVisible = false;

    await this.handleCompanySelector(data);
    await this.buildSmartViews(data.activeViewId);
    await this.buildIntegrityMonitorGrid(data);
  }

  private async handleCompanySelector(data: IntegrityMonitorRulesAppWebComponentSchema) {
    if (this.dependencies.canHaveChildCompanies() && this.dependencies.canModifyRulesAndRuleSets()) {
      this.dom.$companySelector.classList.remove('hidden');
      await this.buildCompanySelector(data);
      return;
    }

    // When no need for selector, hide it and set selected company to the current user's one
    const userCompany = this.dependencies.getCurrentUserCompany();
    this.dependencies.setActiveCompany(userCompany.id, userCompany.name);

    if (!this.dom.$companySelector.classList.contains('hidden')) {
      this.dom.$companySelector.classList.add('hidden');
    }
  }

  private async buildCompanySelector(data: IntegrityMonitorRulesAppWebComponentSchema) {
    this.dom.$companySelector.controller?.view.dom.$companySelectorDropdown.clearInput();
    this.dom.$companySelector.availableCompanies = data.availableCompanies;
    this.updateSelectedCompany(data.availableCompanies);

    if (!this.dom.$companySelector.started) {
      this.dom.$companySelector.injectDependencies(this.dependencies);
      await this.dom.$companySelector.start().catch(console.error);
    } else {
      await (this.dom.$companySelector.controller as any).render();
    }
  }

  private updateSelectedCompany(availableCompanies: Array<Target>) {
    const persistedCompany = this.dependencies.getActiveCompany();
    const persistedCompanyIsValid =
      availableCompanies.findIndex(company => company.companyId === persistedCompany.companyId) > -1;

    if (persistedCompanyIsValid) {
      // Persist the selected company when it is valid
      this.dom.$companySelector.selectedTarget = persistedCompany;
      return;
    }

    // If persisted company is not valid, select the first available company in the selector
    this.dependencies.setActiveCompany(availableCompanies[0].companyId, availableCompanies[0].companyName);
    this.dom.$companySelector.selectedTarget = availableCompanies[0];
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public async buildSmartViews(activeViewId: string) {
    this.dom.$smartViews.activeViewId = activeViewId;

    if (this.dom.$smartViews.started) {
      await this.dom.$smartViews.controller.render();
    } else {
      this.dom.$smartViews.injectDependencies(this.dependencies);
      await this.dom.$smartViews.start().catch(console.error);
    }
  }

  public async buildIntegrityMonitorGrid(data: { activeViewId: string; ruleIdsFilter: Array<string> }) {
    const smartViewItems: Array<SmartViewItemConfiguration<Column, Filter, ViewType>> = [];

    if (!this.dom.$smartViews.sections.length || isEmpty(data)) {
      return;
    }

    this.dom.$smartViews.sections.forEach(section => {
      smartViewItems.push(...section.items);
    });

    const activeSmartViewItem = smartViewItems.filter(item => item.id === data.activeViewId)[0];
    const activeView = this.transformSmartViewToViewConfiguration(activeSmartViewItem);

    if (this.dom.$integrityMonitorGrid.currentView) {
      // If switching from an old view, persist the grid's configuration
      activeView.compactView = this.dom.$integrityMonitorGrid.isCompactViewChecked;
      activeView.columnConfigs = this.dom.$integrityMonitorGrid.columnConfigs;
      activeView.displayedColumns = this.dom.$integrityMonitorGrid.columns;
      activeView.pageSize = this.dom.$integrityMonitorGrid.pageSize;
    }

    this.dom.$integrityMonitorGrid.currentView = activeView;
    this.dom.$integrityMonitorGrid.ruleIdsFilter = data.ruleIdsFilter;
    this.dom.$integrityMonitorGrid.canFetchRulesCountInRuleSet = true;
    this.dom.$integrityMonitorGrid.rulesCountInRuleSet = null;

    if (!this.dom.$integrityMonitorGrid.started) {
      this.dom.$integrityMonitorGrid.injectDependencies(this.dependencies);
      this.dom.$integrityMonitorGrid.start().catch(console.error);
    } else {
      await this.dom.$integrityMonitorGrid.controller.render();
    }
  }

  public showIntegrityMonitorCustomRuleForm() {
    this.dom.$integrityMonitorCustomRuleForm.controller?.view.setLoadingState();
    this.dom.$integrityMonitorCustomRuleForm.reset();
    this.dom.$integrityMonitorCustomRuleForm.isVisible = true;
  }

  public showIntegrityMonitorRuleSetForm() {
    this.dom.$integrityMonitorRuleSetForm.controller?.view.setLoadingState();
    this.dom.$integrityMonitorRuleSetForm.isVisible = true;
    this.dom.$integrityMonitorRuleSetForm.controller?.render();
  }

  public showIntegrityMonitorAssignRulesForm() {
    this.dom.$integrityMonitorAssignRulesForm.controller?.view.setLoadingState();
    this.dom.$integrityMonitorAssignRulesForm.isVisible = true;
    this.dom.$integrityMonitorAssignRulesForm.controller?.render();
  }

  public showIntegrityMonitorDefaultRuleCustomizationForm() {
    this.dom.$integrityMonitorDefaultRuleCustomizationForm.controller?.view.setLoadingState();
    this.dom.$integrityMonitorDefaultRuleCustomizationForm.isVisible = true;
    this.dom.$integrityMonitorDefaultRuleCustomizationForm?.controller.render();
  }

  public getDeleteConfirmationDialogBodyHtml(
    warningText: string,
    numRules: number,
    assignationDataObject: IntegrityMonitorDetailedAssignationData
  ): string {
    const impactSummary = this.getImpactSummary(
      numRules,
      assignationDataObject.numRuleSets ?? 0,
      assignationDataObject.numPolicies ?? 0
    );
    const impactDetails = this.getImpactDetails(assignationDataObject);

    return `<div>
      <p>${warningText}</p>
      <br>
      <p>${impactSummary}</p>
      ${impactDetails.outerHTML}
    </div>`;
  }

  public getDeleteConfirmationDialogFooterHtml(): string {
    return `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
        ${this.dependencies.translateFn('DELETE')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CANCEL')}
      </gz-button>`;
  }

  private getImpactSummary(numRules: number, numRuleSets: number, numPolicies: number): string {
    const fixedValues: any = {
      numRuleSets: `<strong>${numRuleSets}</strong>`,
      numPolicies: `<strong>${numPolicies}</strong>`,
    };

    if (numRules === 1) {
      return this.dependencies.translateFn(
        'This action will impact {numRuleSets} rule sets and {numPolicies} policies.',
        fixedValues
      );
    }

    fixedValues.numRules = `<strong>${numRules}</strong>`;

    return this.dependencies.translateFn(
      'This action will impact {numRules} rules, {numRuleSets} rule sets, and {numPolicies} policies.',
      fixedValues
    );
  }

  private getImpactDetails(assignationDataObject: IntegrityMonitorDetailedAssignationData): HTMLElement {
    const impactDetails = document.createElement('div');

    if (isEmpty(assignationDataObject) || isEmpty(assignationDataObject.assignationData)) {
      return impactDetails;
    }

    // Sort rule sets data by the number of impacted policies in descending order
    assignationDataObject.assignationData.sort(
      (
        assignationItem1: IntegrityMonitoringRuleSetsAndPolicies,
        assignationItem2: IntegrityMonitoringRuleSetsAndPolicies
      ): number => {
        return assignationItem2.policyNames.length - assignationItem1.policyNames.length;
      }
    );

    // Display only the first 5 rule set names
    assignationDataObject.assignationData
      .slice(0, IMConstants.NUM_DISPLAYED_RULE_SETS_ON_DELETE)
      .forEach(assignationItem => {
        const item = document.createElement('div');
        const ruleSetName = this.dependencies.translateFn('Rule set {ruleSetName}', {
          ruleSetName: assignationItem.ruleSetName,
        });
        const affectedPolicyNames = this.getPoliciesListText(assignationItem.policyNames);
        item.innerHTML = ruleSetName + affectedPolicyNames;
        impactDetails.appendChild(document.createElement('br'));
        impactDetails.appendChild(item);
      });

    // If more than 5 rule sets are affected, display "+ X more rule sets" on a new line
    if (assignationDataObject.numRuleSets > IMConstants.NUM_DISPLAYED_RULE_SETS_ON_DELETE) {
      const item = document.createElement('div');
      item.innerHTML = this.dependencies.translateFn('+ {numExtraRuleSets} more rule sets', {
        numExtraRuleSets: assignationDataObject.numRuleSets - IMConstants.NUM_DISPLAYED_RULE_SETS_ON_DELETE,
      });
      impactDetails.appendChild(document.createElement('br'));
      impactDetails.appendChild(item);
    }

    return impactDetails;
  }

  private getPoliciesListText(policies: Array<string>): string {
    if (!policies.length) {
      return '';
    }

    // Display only the first 2 policy names
    const displayedPoliciesText = policies.slice(0, IMConstants.NUM_DISPLAYED_POLICIES_ON_DELETE).join(', ');

    if (policies.length <= IMConstants.NUM_DISPLAYED_POLICIES_ON_DELETE) {
      return (
        ' ' +
        this.dependencies.translateFn('(Policies: {policyNamesList})', {
          policyNamesList: displayedPoliciesText,
        })
      );
    }

    return (
      ' ' +
      this.dependencies.translateFn('(Policies: {policyNamesList} and {numExtraPolicies} more policies)', {
        policyNamesList: displayedPoliciesText,
        numExtraPolicies: policies.length - IMConstants.NUM_DISPLAYED_POLICIES_ON_DELETE,
      })
    );
  }

  private transformSmartViewToViewConfiguration(
    smartViewConfiguration: SmartViewItemConfiguration<Column, Filter, ViewType>
  ): ViewConfiguration<Column, ColumnKeys, ViewType> {
    return {
      id: smartViewConfiguration.id,
      name: smartViewConfiguration.viewName,
      compactView: smartViewConfiguration.isCompactView,
      columnConfigs: smartViewConfiguration.columnConfigs,
      displayedColumns: smartViewConfiguration.displayedColumns,
      filters: smartViewConfiguration.filters,
      pageSize: smartViewConfiguration.pageSize,
      pageNumber: smartViewConfiguration.pageNumber,
      type: smartViewConfiguration.viewType,
    };
  }
}
